import type { BaseSyntheticEvent } from 'react';
import { useState } from 'react';
import {
  useForm,
  type FieldErrors,
  type UseFormRegister,
  type UseFormSetValue,
  type UseFormWatch,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { useUploadLogo } from '../hooks/useUploadLogo';
import { useCreateCompany } from '../hooks/useCreateCompany';
import { createCompanySchema, type CreateCompanyFormData } from '../schemas/company.schemas';
import { transformCompanyFormData } from './CreateCompanyDialog.utils';

interface CreateCompanyHandlersParams {
  logoFile: File | null;
  darkLogoFile: File | null;
  setLogoFile: (file: File | null) => void;
  setDarkLogoFile: (file: File | null) => void;
  reset: () => void;
  onOpenChange: (open: boolean) => void;
}

export function useCreateCompanyHandlers({
  logoFile,
  darkLogoFile,
  setLogoFile,
  setDarkLogoFile,
  reset,
  onOpenChange,
}: CreateCompanyHandlersParams): {
  handleSuccess: (company: { id: string }) => void;
} {
  const { uploadLogo, uploadDarkLogo } = useUploadLogo();

  return {
    handleSuccess: (company: { id: string }): void => {
      if (logoFile) uploadLogo({ companyId: company.id, file: logoFile });
      if (darkLogoFile) uploadDarkLogo({ companyId: company.id, file: darkLogoFile });
      toast.success('Company created successfully');
      reset();
      setLogoFile(null);
      setDarkLogoFile(null);
      onOpenChange(false);
    },
  };
}

interface CreateCompanyForm {
  register: UseFormRegister<CreateCompanyFormData>;
  errors: FieldErrors<CreateCompanyFormData>;
  setValue: UseFormSetValue<CreateCompanyFormData>;
  watch: UseFormWatch<CreateCompanyFormData>;
  onSubmit: (e?: BaseSyntheticEvent) => Promise<void>;
  isLoading: boolean;
  logoFile: File | null;
  darkLogoFile: File | null;
  setLogoFile: (file: File | null) => void;
  setDarkLogoFile: (file: File | null) => void;
}

export function useCreateCompanyForm(onOpenChange: (open: boolean) => void): CreateCompanyForm {
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [darkLogoFile, setDarkLogoFile] = useState<File | null>(null);
  const { createCompany, isLoading } = useCreateCompany();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = useForm<CreateCompanyFormData>({
    resolver: zodResolver(createCompanySchema),
  });
  const { handleSuccess } = useCreateCompanyHandlers({
    logoFile,
    darkLogoFile,
    setLogoFile,
    setDarkLogoFile,
    reset,
    onOpenChange,
  });

  const onSubmit = (data: CreateCompanyFormData): void => {
    createCompany(transformCompanyFormData(data), {
      onSuccess: handleSuccess,
      onError: () => toast.error('Failed to create company'),
    });
  };

  return {
    register,
    errors,
    setValue,
    watch,
    onSubmit: handleSubmit(onSubmit),
    isLoading,
    logoFile,
    darkLogoFile,
    setLogoFile,
    setDarkLogoFile,
  };
}
